"use client";

import { useState, useCallback } from "react";
import { CopyIcon, CheckIcon, MailIcon } from "lucide-react";
import { Button } from "@ui/components/button";
import Image from "next/image";

interface ShareSummaryProps {
  text: string;
  pageUrl: string;
}

const PLATFORMS = [
  {
    id: "x",
    label: "X (Twitter)",
    logo: "/images/twitter-x-logo.svg",
    maxLength: 280,
    buildUrl: (text: string, url: string) => {
      const trimmed = trimToLength(text, 280 - url.length - 2);
      return `https://twitter.com/intent/tweet?text=${enc(trimmed + "\n\n" + url)}`;
    },
  },
  {
    id: "threads",
    label: "Threads",
    logo: "/images/threads-logo.svg",
    buildUrl: (text: string, url: string) =>
      `https://www.threads.net/intent/post?text=${enc(text + "\n\n" + url)}`,
  },
  {
    id: "reddit",
    label: "Reddit",
    logo: "/images/reddit-logo.svg",
    buildUrl: (text: string, url: string) =>
      `https://www.reddit.com/submit?title=${enc(text.split("\n")[0] ?? "")}&url=${enc(url)}&selftext=true&text=${enc(text)}`,
  },
] as const;

function enc(s: string) {
  return encodeURIComponent(s);
}

function trimToLength(text: string, max: number) {
  if (text.length <= max) return text;
  return text.slice(0, max - 1) + "…";
}

export function ShareSummary({ text, pageUrl }: ShareSummaryProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text + "\n\n" + pageUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable
    }
  }, [text, pageUrl]);

  const handleEmail = useCallback(() => {
    const subject = enc(text.split("\n")[0] ?? "AI Visibility Confidence Summary");
    const body = enc(text + "\n\n" + pageUrl);
    window.open(`mailto:?subject=${subject}&body=${body}`, "_self");
  }, [text, pageUrl]);

  const handlePlatform = useCallback(
    (buildUrl: (text: string, url: string) => string) => {
      window.open(buildUrl(text, pageUrl), "_blank", "noopener,noreferrer");
    },
    [text, pageUrl],
  );

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Share the summary text directly — each platform opens with the text pre-filled.
      </p>
      <div className="flex flex-wrap gap-2">
        {PLATFORMS.map((p) => (
          <Button
            key={p.id}
            variant="outline"
            size="sm"
            className="h-8 gap-2 text-xs"
            onClick={() => handlePlatform(p.buildUrl)}
          >
            <Image
              src={p.logo}
              alt={p.label}
              width={16}
              height={16}
              className="h-4 w-4"
            />
            {p.label}
          </Button>
        ))}
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-2 text-xs"
          onClick={handleEmail}
        >
          <MailIcon className="h-4 w-4" />
          Email
        </Button>
        <Button
          variant="secondary"
          size="sm"
          className="h-8 gap-2 text-xs"
          onClick={handleCopy}
        >
          {copied ? (
            <CheckIcon className="h-3.5 w-3.5 text-emerald-500" />
          ) : (
            <CopyIcon className="h-3.5 w-3.5" />
          )}
          {copied ? "Copied!" : "Copy Summary"}
        </Button>
      </div>
    </div>
  );
}
